import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { UserInsuranceProduct } from 'src/app/interfaces/api';
import {
  IInternationalStudentPage,
  IStrapiDocument,
  IStrapiDocuments,
  YourInsurance,
} from 'src/app/interfaces/strapi';

import StrapiService from 'src/app/services/strapi.service';
import { University } from 'src/app/shared/models/university.model';
import { User } from 'src/app/shared/models/user.model';
import { NgIf, NgFor } from '@angular/common';
import { AuthService } from '../../../services/auth.service';

enum MyInsuranceEnum {
  defaultBuddy = '/assets/images/endsleigh/leigh-laptop-sat-down.png',
  internationalStudentBuddy = '/assets/images/buddy_outside_big_ben.png',
}

@Component({
    selector: 'app-my-insurance-section',
    templateUrl: './my-insurance-section.component.html',
    styleUrls: ['./my-insurance-section.component.scss'],
    imports: [
        NgIf,
        NgFor,
        RouterLink,
    ],
})
export class MyInsuranceSectionComponent implements OnInit {
  public MyInsuranceEnum = MyInsuranceEnum;

  public myInsuranceDoc: YourInsurance = <YourInsurance>{};

  public coverageCards: CoverageCard[] = [];

  public userUniversity: University = <University>{};

  public internationalStudentData: IInternationalStudentPage = <
    IInternationalStudentPage
  >{};

  public isLoading: boolean = false;

  public noCoverOrProducts = true;

  @Input() public userInsuranceProducts: UserInsuranceProduct[] = [];

  public card = {
    cta_text: 'View/Update',
    cta_route: '/app/my-insurance',
  } as CoverageCard;

  constructor(
    public auth: AuthService,
    public readonly strapi: StrapiService,
    public readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {}

  private deferUpdate(fn: () => void): void {
    Promise.resolve().then(() => {
      fn();
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.auth.populateUserSubject.subscribe((user: User) => {
      if (!user) return;
      this.deferUpdate(() => {
        this.userUniversity = user.university;
        this.userUniversity.pathway_provider_disclaimer =
          this.userUniversity.pathway_provider_disclaimer?.replaceAll(
            '<name>',
            this.userUniversity.name
          ) ?? '';
      });
    });

    this.auth.getCurrentUser().subscribe((user: User) => {
      if (!user) return;
      this.deferUpdate(() => {
        this.userUniversity = user.university;
      });
    });

    this.strapi
      .getMyInsurance()
      .subscribe((data: IStrapiDocuments<YourInsurance>) => {
        this.deferUpdate(() => {
          this.myInsuranceDoc = data.data[0].attributes;
          this.getInternationalStudentPage();
        });
      });
  }

  private getInternationalStudentPage(): void {
    this.strapi.getInternationalStudentPage().subscribe({
      next: (data: IStrapiDocument<IInternationalStudentPage>) => {
        this.deferUpdate(() => {
          this.internationalStudentData = data.data.attributes;
          this.internationalStudentData.dashboard_banner_content =
            this.internationalStudentData.dashboard_banner_content?.replaceAll(
              '<name>',
              this.userUniversity.name
            ) ?? '';
        });
      },
      error: () => {
        this.afterISFinish();
      },
      complete: () => {
        this.afterISFinish();
      },
    });
  }

  public afterISFinish(): void {
    this.deferUpdate(() => {
      this.processCoverageCards();
      this.isLoading = false;
    });
  }

  private pushInternationalCard(): void {
    if (
      this.userUniversity.is_pathway_provider &&
      Object.keys(this.internationalStudentData).length
    ) {
      this.noCoverOrProducts = false;
      this.coverageCards.push({
        ...this.card,
        title: this.userUniversity?.pathway_provider_title,
        body: this.internationalStudentData.dashboard_banner_content,
        buddy: MyInsuranceEnum.internationalStudentBuddy,
        international_student_card: true,
        cta_text: 'View details',
        cta_route: '/app/international-student',
        show_card: true,
      } as CoverageCard);
    }
  }

  private processCoverageCards(): void {
    this.deferUpdate(() => {
      if (this.auth.currentUser?.accommodation.covered) {
        this.noCoverOrProducts = false;
        this.coverageCards.push({
          ...this.card,
          title: 'Howden for Students Contents Cover',
          body: `${this.auth.currentUser.university.name},${this.auth.currentUser.accommodation.name}`,
          buddy: MyInsuranceEnum.defaultBuddy,
          international_student_card: false,
          show_card: true,
        } as CoverageCard);

        this.pushInternationalCard();
      } else {
        this.pushInternationalCard();
        this.coverageCards.push({
          ...this.card,
          title: 'Cover not included',
          body: this.myInsuranceDoc.no_cover_content_dashboard,
          buddy: MyInsuranceEnum.defaultBuddy,
          international_student_card: false,
          show_card: !this.auth.currentUser?.university.is_unihome_product,
        } as CoverageCard);
      }
    });
  }

  public navigate(route: string): void {
    this.router.navigateByUrl(route).finally(() => false);
  }
}

export interface CoverageCard {
  title: string;
  cta_text: string;
  cta_route: string;
  body: string;
  buddy: string;
  international_student_card: boolean;
  show_card: boolean;
}
